import { useState } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Building2, Search, X } from "lucide-react";
import { fuzzyMatch } from "../../lib/searchUtils";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import AddressFields from "../shared/AddressFields";
import { formatPhoneNumber } from "../../lib/formatters";
const ContractorsTab = ({ generalContractors, getAuthHeaders, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGc, setEditingGc] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    street: "", apt: "", city: "", state: "", zip_code: ""
  });

  const resetForm = () => {
    setForm({ name: "", phone: "", email: "", street: "", apt: "", city: "", state: "", zip_code: "" });
    setEditingGc(null);
  };

  const openEditGc = (gc) => {
    setEditingGc(gc);
    setForm({
      name: gc.name || "",
      phone: gc.phone || "",
      email: gc.email || "",
      street: gc.street || "",
      apt: gc.apt || "",
      city: gc.city || "",
      state: gc.state || "",
      zip_code: gc.zip_code || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGc) {
        await apiClient.put(`/settings/general-contractors/${editingGc.id}`, 
          { ...form, id: editingGc.id }
        );
        toast.success("General contractor updated");
      } else {
        await apiClient.post(`/settings/general-contractors`, 
          { ...form, id: "" }
        );
        toast.success("General contractor added");
      }
      onRefresh();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/settings/general-contractors/${id}`);
      toast.success("General contractor deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete general contractor");
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>General Contractors</CardTitle>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-gc-btn"><Plus className="w-4 h-4 mr-2" />Add Contractor</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingGc ? "Edit General Contractor" : "Add General Contractor"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-group">
                <Label>Company Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} autoComplete="organization" required placeholder="e.g., ABC Construction" data-testid="gc-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Office</Label>
                  <Input value={form.phone} onChange={(e) => setForm({...form, phone: formatPhoneNumber(e.target.value)})} placeholder="(555) 123-4567" maxLength={14} data-testid="gc-phone-input" />
                </div>
                <div className="form-group">
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="e.g., info@abc.com" data-testid="gc-email-input" />
                </div>
              </div>
              
              {/* Address Fields - Using shared component */}
              <AddressFields
                values={{ street: form.street, apt: form.apt, city: form.city, state: form.state, zip_code: form.zip_code }}
                onChange={(field, value) => setForm({...form, [field]: value})}
                onChangeMany={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
                prefix="gc"
                compact={true}
              />
              
              <Button type="submit" className="w-full" data-testid="submit-gc-btn">
                {editingGc ? "Update Contractor" : "Add Contractor"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="p-0">
        <div className="p-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search contractors..." className="pl-9 pr-9" data-testid="contractors-search-input" />
            {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
          </div>
        </div>
        {generalContractors.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Company Name</TableHead>
                <TableHead>Office</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {generalContractors.filter((gc) => {
                if (!search.trim()) return true;
                const searchable = [gc.name, gc.phone, gc.email].filter(Boolean).join(" ");
                return fuzzyMatch(search, searchable);
              }).map((gc) => (
                <TableRow key={gc.id} className="table-row">
                  <TableCell className="font-medium">{gc.name}</TableCell>
                  <TableCell>{gc.phone || "-"}</TableCell>
                  <TableCell>{gc.email || "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openEditGc(gc)} data-testid={`edit-gc-${gc.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive" data-testid={`delete-gc-${gc.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete General Contractor?</AlertDialogTitle>
                            <AlertDialogDescription>This will permanently remove {gc.name}.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(gc.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No general contractors added yet</p>
            <p className="text-sm mt-1">Add contractors to use them in job creation</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ContractorsTab;
